import useProgramFromParams from '@/app/hooks/useProgramFromParams'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { useNavigation } from '@/app/hooks'
import { useEffect, useMemo, useState } from 'react'
import { ID } from '@/types/model'
import { canCharge } from '@/utils/vehicles/permissions'
import { programWithEvaluatedConfigTemplateFromUtilityNew } from '@/reducers/utilityPrograms'
import { UtilityProgramEnrollment } from '@/types'
import { utilityProgramEnrollmentCollection } from '@/reducers/utilityProgramEnrollments'
import { useProfile } from '@/hooks/useProfileId'
import Sentry from '@/logging/sentry'
import useEnrolledDeviceSelections, {
  NextDevice,
} from '@/app/hooks/useEnrolledDeviceSelections'
import { vehicleDescriptionSelector } from '@/app/features/utility-programs/UtilityProgramEligibleDeviceEnrollmentFlowStep/useViewModel'
import { programsNewCollection } from '@/reducers/programsNew'
import useEnrollmentActions from '@/hooks/useEnrollmentActions'
import { useSurveyResponder } from '@/app/components/SurveyObserver'
import useExternalNavigation from '@/app/hooks/useExternalNavigation'

export default function useViewModel() {
  const {
    utility,
    utilityProgram,
    eligibilityOverview,
    programId,
    isLoading,
    enrollmentOverview,
  } = useProgramFromParams()
  const navigate = useNavigation()
  const [selectedVehicles, setSelectedVehicles] = useState<ID[]>([])
  const [selectedChargers, setSelectedChargers] = useState<ID[]>([])
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [isEnrolling, setIsEnrolling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedBehavioralEnrollment, setSelectedBehavioralEnrollment] =
    useState(true)
  const { data: programsData } = programsNewCollection.useFetch()
  const { enrollDevices } = useEnrollmentActions()
  const { completeSurvey: completeOnboarding } =
    useSurveyResponder('Onboarding')

  const dispatch = useAppDispatch()
  const profile = useProfile()
  const { createEnrolledDeviceParams } = useEnrolledDeviceSelections()
  const { externallyNavigate } = useExternalNavigation()

  const enrolledChargerIds = useMemo(() => {
    return enrollmentOverview?.charger_enrollments
      ?.filter((enrollment) => {
        return (
          enrollment.enrolled && +enrollment.utility_program_id === +programId
        )
      })
      ?.map((enrollment) => enrollment.charger_id)
  }, [enrollmentOverview])

  const enrolledVehicleIds = useMemo(() => {
    return enrollmentOverview?.vehicle_enrollments
      ?.filter((enrollment) => {
        return (
          enrollment.enrolled && +enrollment.utility_program_id === +programId
        )
      })
      ?.map((enrollment) => enrollment.vehicle_id)
  }, [enrollmentOverview])

  const eligibleChargers = useMemo(() => {
    return eligibilityOverview?.devices?.chargers
      ?.filter((chargerEligibility) => chargerEligibility.charger !== null)
      ?.filter((chargerEligibility) => chargerEligibility.is_eligible)
      ?.map((chargerEligibility) => chargerEligibility.charger)
      ?.filter((charger) => !enrolledChargerIds?.includes(charger.id))
  }, [eligibilityOverview, enrolledChargerIds])

  const eligibleVehicles = useMemo(() => {
    return eligibilityOverview?.devices?.vehicles
      ?.filter((vehicleEligibility) => vehicleEligibility.vehicle !== null)
      ?.filter((vehicleEligibility) => vehicleEligibility.is_eligible)
      ?.map((vehicleEligibility) => vehicleEligibility.vehicle)
      ?.filter((vehicle) => !enrolledVehicleIds?.includes(vehicle.id))
  }, [eligibilityOverview, enrolledVehicleIds])

  const ineligibleVehicleIds = eligibilityOverview?.devices?.vehicles
    ?.filter((vehicle) => vehicle.vehicle !== null)
    ?.filter((vehicle) => !vehicle.is_eligible)
    ?.map((vehicle) => vehicle.id)

  const ineligibleVehicles = useAppSelector(
    vehicleDescriptionSelector(ineligibleVehicleIds ?? []),
  )

  const eligibleForBehavioralEnrollment =
    programsData[0]?.device_eligibility_overview?.profile?.is_eligible &&
    programsData[0]?.device_eligibility_overview?.devices?.behavioral &&
    programsData[0]?.device_limits.behavioral &&
    programsData[0]?.device_eligibility_overview?.devices?.behavioral.every(
      (b) => b.is_eligible,
    )

  useEffect(
    function initializeSelectedDevices() {
      const eligibleVehicleIds = eligibleVehicles?.map((vehicle) => vehicle.id)
      setSelectedVehicles(eligibleVehicleIds ?? [])
    },
    [eligibleVehicles],
  )

  useEffect(
    function initializeSelectedChargers() {
      const eligibleChargerIds = eligibleChargers?.map((charger) => charger.id)
      setSelectedChargers(eligibleChargerIds ?? [])
    },
    [eligibleChargers],
  )

  const controlledVehicles = eligibleVehicles?.filter((vehicle) =>
    canCharge(vehicle),
  )
  const passiveVehicles = eligibleVehicles?.filter(
    (vehicle) => !canCharge(vehicle),
  )

  const noEligibleVehiclesOrChargers =
    (!eligibleVehicles || eligibleVehicles.length === 0) &&
    (!eligibleChargers || eligibleChargers.length === 0)
  const address = profile?.home?.formatted_address ?? 'Address Not Available'

  const isSkippingEnrollment = () => {
    if (noEligibleVehiclesOrChargers) {
      return !eligibleForBehavioralEnrollment || !selectedBehavioralEnrollment
    }

    return selectedVehicles.length === 0 && selectedChargers.length === 0
  }

  const viewConfig = utilityProgram
    ? programWithEvaluatedConfigTemplateFromUtilityNew(utilityProgram, {
        utility_name: utility?.name ?? '',
      }).view_config_json.eligibleDeviceEnrollment
    : null

  const tosConfig = utilityProgram
    ? programWithEvaluatedConfigTemplateFromUtilityNew(utilityProgram, {
        utility_name: utility?.name ?? '',
      }).view_config_json.enrollmentConfirmationModal
    : null

  const ineligibleForEnrollment =
    noEligibleVehiclesOrChargers && !eligibleForBehavioralEnrollment

  const viewConfigTitle = viewConfig?.title?.text ?? 'Utility Program'
  const pageTitle = ineligibleForEnrollment
    ? 'No Eligible Devices Found'
    : viewConfigTitle

  const toggleVehicleEnroll = (vehicleId: ID) => {
    setSelectedVehicles((prevState) => {
      if (prevState.includes(vehicleId)) {
        return prevState.filter((id) => id !== vehicleId)
      }

      return [...prevState, vehicleId]
    })
  }

  const toggleChargerEnroll = (chargerId: ID) => {
    setSelectedChargers((prevState) => {
      if (prevState.includes(chargerId)) {
        return prevState.filter((id) => id !== chargerId)
      }

      return [...prevState, chargerId]
    })
  }

  const onEnroll = async () => {
    if (isSkippingEnrollment()) {
      completeOnboarding()
      navigate.push('/app')
      return
    }

    if (!tosConfig) {
      await acceptAndEnroll()
      return
    }

    setShowConfirmationModal(true)
  }

  const acceptAndEnroll = async () => {
    setShowConfirmationModal(false)
    setIsEnrolling(true)
    setError(null)

    // Behavioral enrollment
    if (noEligibleVehiclesOrChargers) {
      const behavioralEnrollment = await enrollBehaviorally()
      if (!behavioralEnrollment) {
        setIsEnrolling(false)
        return
      }
    } else {
      const result = await enrollDevices({
        utilityProgramId: +programId,
        vehicleIds: selectedVehicles,
        thermostatIds: [],
        chargerIds: selectedChargers,
      })

      if (result.isErr()) {
        setError(
          `An error occurred when enrolling in this program: ${result.error.message}. If possible, correct this error and try again.`,
        )
        setIsEnrolling(false)
        return
      }
    }

    let nextDevice: NextDevice | null = null
    if (selectedVehicles?.[0]) {
      nextDevice = { type: 'vehicle', id: selectedVehicles[0] }
    } else if (selectedChargers?.[0]) {
      nextDevice = { type: 'vehicle-charger', id: selectedChargers[0] }
    } else if (selectedBehavioralEnrollment && profile) {
      nextDevice = { type: 'home', id: profile.home.id }
    }

    navigate.push(`success${createEnrolledDeviceParams(nextDevice)}`)
  }

  const enrollBehaviorally =
    async (): Promise<UtilityProgramEnrollment | null> => {
      if (!utilityProgram) {
        return null
      }
      const result = await dispatch(
        utilityProgramEnrollmentCollection.actions.create({
          utility_program_id: utilityProgram.id,
          vehicle_ids: [],
          thermostat_ids: [],
          charger_ids: [],
          enroll_behaviorally: noEligibleVehiclesOrChargers,
        }),
      )

      if (result.error) {
        const errorMessage =
          result.payload.response?.[0] ||
          result.payload.response?.error_message ||
          'Enrollment error'

        Sentry.logMessage(errorMessage)
        setError(
          'There was an error while enrolling. You may not meet the eligibility requirements for this program.',
        )
        return null
      }

      return result.payload
    }

  const toggleBehavioralEnrollment = () => {
    setSelectedBehavioralEnrollment((prev) => !prev)
  }

  const closeModal = () => {
    setShowConfirmationModal(false)
  }

  const pageSubtitle = () => {
    if (isLoading || ineligibleForEnrollment) {
      return ''
    }
    if (noEligibleVehiclesOrChargers) {
      // TODO DEV-2992 -- We need to update this to pull from a new CMS field
      return 'You are enrolling your address only, which earns $75 upfront plus $25 per year through 2026, no matter how many vehicles charge at the address.'
    }
    return (
      viewConfig?.description?.text ?? 'Select eligible devices for enrollment'
    )
  }

  const connectAnotherDevice = () => {
    navigate.push('/ava/connect-vehicle?onboarding=true')
  }

  const navigateToEligibleDevices = () => {
    externallyNavigate(
      'https://get.optiwatt.com/ava-smarthome-charging-terms/#eligibleVehicles',
    )
  }

  return {
    error,
    isEnrolling,
    showConfirmationModal,
    selectedVehicles,
    controlledVehicles,
    passiveVehicles,
    ineligibleVehicles,
    selectedChargers,
    eligibleChargers,
    tosConfig,
    viewConfig,
    toggleVehicleEnroll,
    toggleChargerEnroll,
    closeModal,
    onEnroll,
    acceptAndEnroll,
    behavioralEnrollment: noEligibleVehiclesOrChargers,
    ineligibleForEnrollment,
    address,
    pageTitle: isLoading ? 'Loading program information...' : pageTitle,
    pageSubtitle: pageSubtitle(),
    toggleBehavioralEnrollment,
    selectedBehavioralEnrollment,
    isSkippingEnrollment: isSkippingEnrollment(),
    connectAnotherDevice,
    navigateToEligibleDevices,
  }
}
